import { useEffect, useRef, useState } from 'react';
import { Alert } from './api';
import { useAuthStore } from '../store/authStore';

const WS_BASE =
  process.env.REACT_APP_WS_BASE ??
  `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;
const RECONNECT_DELAY_MS = 4_000;

/**
 * Maintains a WebSocket connection to /api/alerts/ws.
 * Calls onNewAlert for every inbound message. Reconnects automatically.
 * If the handshake fails and /api/auth/me returns 401/403, calls logout().
 * Returns true while the socket is open.
 */
export function useAlertsSocket(
  token: string | null,
  onNewAlert: (alert: Alert) => void,
): boolean {
  const [connected, setConnected] = useState(false);
  const logout = useAuthStore((s) => s.logout);
  // Stable ref so the effect doesn't re-run on every render
  const callbackRef = useRef(onNewAlert);
  callbackRef.current = onNewAlert;

  useEffect(() => {
    if (!token) return;

    let ws: WebSocket | null = null;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let hasOpened = false;

    const connect = () => {
      if (cancelled) return;
      hasOpened = false;
      ws = new WebSocket(`${WS_BASE}/api/alerts/ws?token=${encodeURIComponent(token)}`);

      ws.onopen = () => {
        hasOpened = true;
        setConnected(true);
      };

      ws.onmessage = (evt) => {
        try {
          const alert: Alert = JSON.parse(evt.data as string);
          callbackRef.current(alert);
        } catch {
          // ignore malformed frames
        }
      };

      ws.onerror = () => ws?.close();

      ws.onclose = async () => {
        setConnected(false);
        if (cancelled) return;

        // Handshake never completed — backend rejects expired/invalid JWT with HTTP 403
        // before WS upgrade; onclose fires with code 1006, the custom 4401 never reaches
        // the client. Verify via REST to distinguish auth failure from a transient blip.
        if (!hasOpened) {
          try {
            const resp = await fetch('/api/auth/me', {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (resp.status === 401 || resp.status === 403) {
              cancelled = true;
              logout();
              return;
            }
          } catch {
            // network error — fall through to retry
          }
        }

        retryTimer = setTimeout(connect, RECONNECT_DELAY_MS);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      ws?.close();
    };
  }, [token, logout]);

  return connected;
}
